import React, { useCallback, useMemo, useState } from "react";
import ReactGrid, { Table } from "@devexpress/dx-react-grid-bootstrap4";
import CrudList, { defaultListMessages, StyledTableRow, TableColumns } from "../shared/crud/list";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { mapRenderer, StringRenderer } from "../shared/renderers/table";
import {
  CancellationPolicyDetailsRenderer,
  CustomDateTimeRenderer,
} from "../historicalReservation/renders";
import { RendererProps } from "../shared/renderers";
import styled from "styled-components";
import { isAfter } from "date-fns";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";
import { useHistory } from "react-router";
import { automaticChargesMessages } from "./messages";
import { usePaymentApiClient } from "../../lib/http";
import { useAutomaticChargesApi } from "./infraestructure/automaticCharges.api";
import { AutomaticChargeEntity } from "./infraestructure/automaticCharges.dto";
import {
  generateAutomaticChargeRequest,
  useAutomaticChargeFilters,
} from "./application/automaticCharges.service";
import { usePropertyCurrencies } from "../report/infrastructure/report.api";
import { Button, ButtonProps, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TableRowActionProps } from "../shared/actions";
import { FormattedMessage, useIntl } from "react-intl";
import {
  faArrowUpRightFromSquare,
  faTriangleExclamation,
  faCircleExclamation,
  faRotateRight,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { APP_COLORS, CHARGE_ALERTS, CHARGE_STATUS } from "../witadmin/Common/constants";
import SweetAlert from "sweetalert2";
import { reservationMessages } from "../reservation/reservation.messages";
import { StyledSpinner } from "../witadmin/CRUD/EntityForm/EntityForm";

const StyledLink = styled.button`
  color: inherit;

  &:hover {
    color: inherit;
    text-decoration: underline;
  }
`;

const ActionDiv = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  margin-left: 8px;
  vertical-align: middle;
  svg {
    width: 13px;
    height: 13px;
  }
`;

const CustomStyledTableRow = styled(StyledTableRow)`
  ${({ row }: { row: AutomaticChargeEntity }) => {
    if (row.chargeStatus === CHARGE_STATUS.OK) {
      return `color: ${APP_COLORS["success"]}`;
    }

    if (row.chargeStatus === CHARGE_STATUS.KO) {
      return `color: ${APP_COLORS["danger"]}`;
    }

    if (row.chargeStatus === CHARGE_STATUS.PEND) {
      return `color: ${APP_COLORS["warning"]}`;
    }

    if (row.chargeStatus === CHARGE_STATUS.CXL) {
      return `color: ${APP_COLORS["gray-darkish"]}`;
    }
  }}
`;

const defaultPageSize = 50;
const pageSizes = [50, 100, 500];

const tooltipConfig = {
  delay: { show: 250, hide: 0 },
};

interface ButtonConfig extends ButtonProps {
  className: string;
}

const buttonConfig: ButtonConfig = {
  disabled: false,
  size: "sm",
  className: "mr-2",
  variant: "light",
};

const goToReservationTooltip = props => (
  <Tooltip id="go-reservation-tooltip" {...props}>
    <FormattedMessage {...automaticChargesMessages.goToReservation} />
  </Tooltip>
);

const modifiedReservationTooltip = props => (
  <Tooltip id="go-reservation-tooltip" {...props}>
    <FormattedMessage {...automaticChargesMessages.modifiedReservation} />
  </Tooltip>
);

const failedPaymentTooltip = props => (
  <Tooltip id="go-reservation-tooltip" {...props}>
    <FormattedMessage {...automaticChargesMessages.failedPayment} />
  </Tooltip>
);

const cannotBeChargedTooltip = props => (
  <Tooltip id="go-reservation-tooltip" {...props}>
    <FormattedMessage {...automaticChargesMessages.cannotBeCharged} />
  </Tooltip>
);

const retryChargeTooltip = props => (
  <Tooltip id="go-reservation-tooltip" {...props}>
    <FormattedMessage {...automaticChargesMessages.retryCharge} />
  </Tooltip>
);

const cancelChargeTooltip = props => (
  <Tooltip id="go-reservation-tooltip" {...props}>
    <FormattedMessage {...automaticChargesMessages.cancelCharge} />
  </Tooltip>
);

function AutomaticChargeList({ url, propertyTicker }: { url: string; propertyTicker: string }) {
  const [retryChargeLoading, setRetryChargeLoading] = useState("");
  const [cancelChargeLoading, setCancelChargeLoading] = useState("");
  const propertyConfiguration = usePropertyConfiguration();
  const history = useHistory();
  const intl = useIntl();
  const apiClient = usePaymentApiClient();
  const {
    getAutomaticCharges,
    getCollectAutomaticCharges,
    cancelAutomaticCharge,
  } = useAutomaticChargesApi(apiClient);
  const lastSlashIndex = useMemo(() => url.lastIndexOf("/"), [url]);
  const urlNoTicker = useMemo(() => url.substring(0, lastSlashIndex), [url, lastSlashIndex]);
  const filters = useAutomaticChargeFilters();
  const { defaultCurrency } = usePropertyCurrencies();
  const [forceUpdate, setForceUpdate] = useState(0);

  const goToReservation = useCallback(
    props => {
      history.push(`${urlNoTicker}/${propertyTicker}/reservation-detail/${props.bookingId}`);
    },
    [history, urlNoTicker, propertyTicker]
  );

  const IdRenderer = useCallback(
    ({ value, rows }: RendererProps<string>) => {
      return <StyledLink onClick={() => goToReservation(rows)}>{value}</StyledLink>;
    },
    [goToReservation]
  );

  const formattedColumns: TableColumns<AutomaticChargeEntity> = useMemo(
    () => ({
      bookingId: {
        renderer: IdRenderer,
        width: 125,
      },
      multipleBookingId: {
        renderer: IdRenderer,
        width: 100,
      },
      status: {
        renderer: StringRenderer,
        width: 150,
      },
      cancellationPolicy: {
        renderer: mapRenderer(CancellationPolicyDetailsRenderer, v => v, {
          defaultCurrency,
        }),
        width: 150,
      },
      paymentType: {
        renderer: ({ value, ...props }: RendererProps<string>) =>
          StringRenderer({
            value:
              value && reservationMessages[value.toLowerCase()]
                ? intl.formatMessage(reservationMessages[value.toLowerCase()])
                : value,
            ...props,
          }),
        width: 150,
      },
      estimatedCollectDate: {
        renderer: mapRenderer(CustomDateTimeRenderer, (v: string | undefined) => v, {
          timeZone: propertyConfiguration?.timeZone,
        }),
        width: 200,
      },
      jobLastExecution: {
        renderer: mapRenderer(CustomDateTimeRenderer, (v: string | undefined) => v, {
          timeZone: propertyConfiguration?.timeZone,
        }),
        width: 200,
      },
      effectiveCollectDate: {
        renderer: mapRenderer(CustomDateTimeRenderer, (v: string | undefined) => v, {
          timeZone: propertyConfiguration?.timeZone,
        }),
        width: 200,
      },
      chargeStatus: {
        renderer: StringRenderer,
        width: 150,
      },
      intents: {
        renderer: StringRenderer,
        width: 100,
      },
      amount: {
        renderer: ({ rows }) => StringRenderer({ value: rows?.amount?.toFixed(2) }),
        width: 100,
      },
      currency: {
        renderer: StringRenderer,
        width: 100,
      },
    }),
    [IdRenderer, propertyConfiguration?.timeZone, defaultCurrency, intl]
  );

  const getAllAutomaticCharges = useCallback(
    async ({ currentPage, filterValues, pageSize, setTotalRowsCount }) => {
      try {
        const { request, size, page } = generateAutomaticChargeRequest({
          filterValues,
          pageSize,
          currentPage,
        });
        const result = await getAutomaticCharges({ propertyTicker, request, size, page });
        setTotalRowsCount(result.pagination.total);
        return result.content;
      } catch (e) {
        console.error("Error get automatic charges", e);
      }
    },
    [getAutomaticCharges, propertyTicker]
  );

  const processRetry = useCallback(
    async row => {
      try {
        setRetryChargeLoading(row.id);
        await getCollectAutomaticCharges({ id: row.id });
      } catch (error) {
      } finally {
        await SweetAlert.fire({
          icon: "info",
          text: intl.formatMessage(automaticChargesMessages.retryChargeSuccess),
          cancelButtonText: `${intl.formatMessage(automaticChargesMessages.back)}`,
        });
        setForceUpdate(prevState => prevState + 1);
        setRetryChargeLoading("");
      }
    },
    [getCollectAutomaticCharges, intl]
  );

  const checkIfThereAreAtLestFiveMinutesFromLastExecution = useCallback(row => {
    if (!row.jobLastExecution) {
      return true;
    }
    const lastExecutionTime = new Date(row.jobLastExecution);
    const currentTime = new Date();

    const timeDifferenceInMinutes =
      (currentTime.getTime() - lastExecutionTime.getTime()) / (1000 * 60);

    return timeDifferenceInMinutes >= 5;
  }, []);

  const confirmRetryCharge = useCallback(
    async row => {
      if (!row.jobLastExecution) {
        return;
      }

      if (!checkIfThereAreAtLestFiveMinutesFromLastExecution(row)) {
        await SweetAlert.fire({
          icon: "warning",
          text: intl.formatMessage(automaticChargesMessages.retryChargeTimeValidation),
          cancelButtonText: `${intl.formatMessage(automaticChargesMessages.back)}`,
        });
        return;
      }

      await SweetAlert.fire({
        icon: "question",
        showCancelButton: true,
        text: intl.formatMessage(automaticChargesMessages.retryChargeMultipleBookingConfirmation, {
          multipleBookingId: row.multipleBookingId,
        }),
        confirmButtonColor: APP_COLORS.success,
        cancelButtonText: intl.formatMessage(automaticChargesMessages.cancel),
      }).then(result => {
        if (result.isConfirmed) {
          processRetry(row);
        }
      });
    },
    [intl, processRetry, checkIfThereAreAtLestFiveMinutesFromLastExecution]
  );

  const cancelCharge = useCallback(
    async row => {
      try {
        setCancelChargeLoading(row.id);
        await cancelAutomaticCharge({ id: row.id });
        setForceUpdate(prevState => prevState + 1);
        await SweetAlert.fire({
          icon: "success",
          text: intl.formatMessage(automaticChargesMessages.cancelChargeSuccess),
          cancelButtonText: `${intl.formatMessage(automaticChargesMessages.back)}`,
        });
      } catch (error) {
        await SweetAlert.fire({
          icon: "error",
          text: intl.formatMessage(automaticChargesMessages.cancelChargeError),
          cancelButtonText: `${intl.formatMessage(automaticChargesMessages.back)}`,
        });
      } finally {
        setCancelChargeLoading("");
      }
    },
    [cancelAutomaticCharge, intl]
  );

  const TableRowActions = useCallback(
    (props: TableRowActionProps<any>) => {
      const estimatedCollectDate = props.row.estimatedCollectDate
        ? new Date(props.row.estimatedCollectDate)
        : null;
      const currentDate = new Date();
      const isFutureOrder = estimatedCollectDate
        ? isAfter(estimatedCollectDate, currentDate)
        : false;

      return (
        <Table.Cell {...props.rest}>
          {/*Detail*/}
          <OverlayTrigger {...tooltipConfig} overlay={goToReservationTooltip}>
            <Button {...buttonConfig} onClick={() => goToReservation(props.row)}>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </Button>
          </OverlayTrigger>
          {props.row.chargeAlert === CHARGE_ALERTS.MODIFIED_BY_RESERVATION && (
            <OverlayTrigger {...tooltipConfig} overlay={modifiedReservationTooltip}>
              <ActionDiv>
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </ActionDiv>
            </OverlayTrigger>
          )}
          {props.row.chargeAlert === CHARGE_ALERTS.CHARGE_FAILED && (
            <OverlayTrigger {...tooltipConfig} overlay={failedPaymentTooltip}>
              <ActionDiv>
                <FontAwesomeIcon icon={faCircleExclamation} />
              </ActionDiv>
            </OverlayTrigger>
          )}
          {props.row.chargeAlert === CHARGE_ALERTS.CANNOT_CHARGED && (
            <OverlayTrigger {...tooltipConfig} overlay={cannotBeChargedTooltip}>
              <ActionDiv>
                <span style={{ fontSize: "15px" }} className="material-symbols-outlined">
                  credit_card_off
                </span>
              </ActionDiv>
            </OverlayTrigger>
          )}
          {props.row.chargeStatus === CHARGE_STATUS.KO && (
            <OverlayTrigger {...tooltipConfig} overlay={retryChargeTooltip}>
              <Button
                {...buttonConfig}
                disabled={retryChargeLoading === props.row.id}
                onClick={() => confirmRetryCharge(props.row)}>
                {retryChargeLoading === props.row.id ? (
                  <StyledSpinner className="fa fa-spinner fa-spin" />
                ) : (
                  <FontAwesomeIcon icon={faRotateRight} />
                )}
              </Button>
            </OverlayTrigger>
          )}
          {props.row.chargeStatus === CHARGE_STATUS.PEND && isFutureOrder && (
            <OverlayTrigger {...tooltipConfig} overlay={cancelChargeTooltip}>
              <Button
                {...buttonConfig}
                disabled={cancelChargeLoading === props.row.id}
                onClick={() => cancelCharge(props.row)}>
                {cancelChargeLoading === props.row.id ? (
                  <StyledSpinner className="fa fa-spinner fa-spin" />
                ) : (
                  <FontAwesomeIcon icon={faXmark} />
                )}
              </Button>
            </OverlayTrigger>
          )}
        </Table.Cell>
      );
    },
    [goToReservation, confirmRetryCharge, retryChargeLoading, cancelCharge, cancelChargeLoading]
  );

  const customRow = useCallback(
    (rows: AutomaticChargeEntity[] = []) => ({
      row,
      ...restProps
    }: ReactGrid.Table.DataRowProps) => {
      return <CustomStyledTableRow {...restProps} row={row} index={rows.indexOf(row)} />;
    },
    []
  );

  return (
    <CrudList<AutomaticChargeEntity, {}>
      messages={{
        ...defaultListMessages,
        ...automaticChargesMessages,
        tableHeaders: automaticChargesMessages,
      }}
      columns={formattedColumns}
      onGetAll={getAllAutomaticCharges}
      paging
      pageSizes={pageSizes}
      defaultPageSize={defaultPageSize}
      filters={filters}
      managerName={"automaticCharge"}
      filtersClosedByDefault={false}
      hideExportButton
      rowActions={props => <TableRowActions {...props} />}
      customRow={customRow}
      forceLoad={forceUpdate}
    />
  );
}

export default withAuthorizationVerifier(AutomaticChargeList);
